import { FC, ReactNode } from 'react'
import Card from 'components/cards/base/Card'
import { Format } from 'common/utils'
import { getPlanPrice } from 'common/utils/planPrice'
import { PlanSlug } from 'common/planSlugs'
import { Tenant, tenantToSlug } from 'common/tenant'

type KeyBenefitsSectionType = {
  tenant?: Tenant
}

type BenefitItem = {
  content: ReactNode
  icon: ReactNode
  title: string
  /** Optional max-width for the content area (e.g. '20rem', '400px', '50ch') */
  contentMaxWidth?: string
}

const getLocationContent = (tenant: Tenant) => ({
  businessAddress:
    tenant === Tenant.AU
      ? 'Sydney CBD business address'
      : 'prime London business address',
  facilityText:
    tenant === Tenant.AU
      ? 'Secure central Sydney facility'
      : 'Secure central London facility',
  location: tenant === Tenant.AU ? 'Sydney' : 'London',
})

const getBenefits = (tenant: Tenant): BenefitItem[] => {
  const { businessAddress, facilityText, location } = getLocationContent(tenant)
  const planPrice = getPlanPrice(tenantToSlug(tenant))

  return [
    {
      content: (
        <span>
          Get the best virtual office {location} has to offer with a Hoxton Mix
          Virtual Office Plan. One simple plan, giving you all the benefits of a{' '}
          {businessAddress} for just{' '}
          <span className='fw-bold'>
            {planPrice.monthlyOnYearlyPlan(PlanSlug.SCAN_LITE)}
            /month
          </span>{' '}
          (when paid annually).
        </span>
      ),
      icon: (
        <svg
          width='44'
          height='44'
          viewBox='0 0 44 44'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M34.2222 19.5556V0H9.77778V9.77778H0V44H19.5556V34.2222H24.4444V44H44V19.5556H34.2222ZM9.77778 39.1111H4.88889V34.2222H9.77778V39.1111ZM9.77778 29.3333H4.88889V24.4444H9.77778V29.3333ZM9.77778 19.5556H4.88889V14.6667H9.77778V19.5556ZM19.5556 29.3333H14.6667V24.4444H19.5556V29.3333ZM19.5556 19.5556H14.6667V14.6667H19.5556V19.5556ZM19.5556 9.77778H14.6667V4.88889H19.5556V9.77778ZM29.3333 29.3333H24.4444V24.4444H29.3333V29.3333ZM29.3333 19.5556H24.4444V14.6667H29.3333V19.5556ZM29.3333 9.77778H24.4444V4.88889H29.3333V9.77778ZM39.1111 39.1111H34.2222V34.2222H39.1111V39.1111ZM39.1111 29.3333H34.2222V24.4444H39.1111V29.3333Z'
            fill='url(#paint0_linear_3836_7722)'
          />
          <defs>
            <linearGradient
              id='paint0_linear_3836_7722'
              x1='-2.22138e-07'
              y1='44'
              x2='50.5874'
              y2='9.72149'
              gradientUnits='userSpaceOnUse'
            >
              <stop stopColor='#BE33A1' />
              <stop offset='1' stopColor='#FF4F7B' />
            </linearGradient>
          </defs>
        </svg>
      ),
      title: 'A prestigious business address without the overheads',
    },
    {
      content:
        'Security and privacy are built into everything we do: At Hoxton Mix, we take the responsibility of processing mail seriously, so you can feel confident that private and confidential information will remain so.',
      contentMaxWidth: '490px',
      icon: (
        <svg
          width='44'
          height='44'
          viewBox='0 0 44 44'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M22 0C34.1506 0 44 9.8494 44 22C44 34.1506 34.1506 44 22 44C18.2556 44 14.729 43.065 11.6424 41.415L0 44L2.5872 32.362C0.9372 29.2732 0 25.7466 0 22C0 9.8494 9.8494 0 22 0ZM22 4.4C12.2804 4.4 4.4 12.2804 4.4 22C4.4 24.937 5.1172 27.7596 6.468 30.2852L7.238 31.724L5.7948 38.2052L12.2804 36.7664L13.717 37.5342C16.2426 38.885 19.063 39.6 22 39.6C31.7196 39.6 39.6 31.7196 39.6 22C39.6 12.2804 31.7196 4.4 22 4.4ZM22 11C25.6454 11 28.6 13.9546 28.6 17.6V19.8H30.8V30.8H13.2V19.8H15.4V17.6C15.4 13.9546 18.3546 11 22 11ZM26.4 24.2H17.6V26.4H26.4V24.2ZM22 15.4C20.7856 15.4 19.8 16.39 19.8 17.6V19.8H24.2V17.6C24.2 16.3856 23.2144 15.4 22 15.4Z'
            fill='#FF0004'
          />
        </svg>
      ),
      title: 'Protect your privacy',
    },
    {
      content:
        'A secure facility that has 24/7 monitoring and restricted access technology.',
      contentMaxWidth: '530px',
      icon: (
        <svg
          width='54'
          height='44'
          viewBox='0 0 54 44'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M41.591 44.0003H0.226562V39.8098H2.29479V8.38105C2.29479 7.82535 2.51269 7.29242 2.90055 6.89948C3.28842 6.50655 3.81448 6.2858 4.36301 6.2858H33.3181C33.8667 6.2858 34.3927 6.50655 34.7806 6.89948C35.1685 7.29242 35.3864 7.82535 35.3864 8.38105V18.8573H39.5228V39.8098H41.591V44.0003ZM31.2499 39.8098H35.3864V23.0478H22.977V39.8098H27.1135V27.2383H31.2499V39.8098ZM31.2499 18.8573V10.4763H6.43123V39.8098H18.8406V18.8573H31.2499ZM10.5677 23.0478H14.7041V27.2383H10.5677V23.0478ZM10.5677 31.4288H14.7041V35.6193H10.5677V31.4288ZM10.5677 14.6668H14.7041V18.8573H10.5677V14.6668Z'
            fill='#3F64FB'
          />
          <path
            d='M51.9307 8.38099H53.9989V16.762H37.4531V8.38099H39.5213V6.28575C39.5213 4.61866 40.1751 3.01986 41.3387 1.84105C42.5023 0.662246 44.0804 0 45.726 0C47.3716 0 48.9498 0.662246 50.1134 1.84105C51.277 3.01986 51.9307 4.61866 51.9307 6.28575V8.38099ZM47.7942 8.38099V6.28575C47.7942 5.73005 47.5763 5.19712 47.1885 4.80418C46.8006 4.41125 46.2745 4.1905 45.726 4.1905C45.1775 4.1905 44.6514 4.41125 44.2636 4.80418C43.8757 5.19712 43.6578 5.73005 43.6578 6.28575V8.38099H47.7942Z'
            fill='#3F64FB'
          />
        </svg>
      ),
      title: facilityText,
    },
    {
      content:
        'Fit and proper background checks and extensive training of all employees.',
      contentMaxWidth: '410px',
      icon: (
        <svg
          width='38'
          height='45'
          viewBox='0 0 38 45'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M20.2742 27.8173V32.0467C18.4393 31.3991 16.4754 31.2005 14.5476 31.4675C12.6198 31.7345 10.7843 32.4593 9.19524 33.581C7.60621 34.7028 6.31004 36.1887 5.41558 37.9141C4.52112 39.6394 4.05448 41.5538 4.05485 43.4964L5.24001e-07 43.4944C-0.000628733 41.0233 0.565491 38.5849 1.65498 36.3659C2.74448 34.147 4.32842 32.2064 6.28545 30.6929C8.24249 29.1795 10.5207 28.1332 12.9454 27.6344C15.3702 27.1356 17.8771 27.1975 20.2742 27.8153V27.8173ZM16 25.0473C9.27909 25.0473 4 19.7084 4 13C4 6.29163 9.27909 0.95272 16 0.95272C22.7209 0.95272 28 6.29163 28 13C28 19.7084 22.7209 25.0473 16 25.0473ZM16 21C20.4806 21 24 17.4722 24 13C24 8.52776 20.4806 5 16 5C11.5194 5 8 8.52776 8 13C8 17.4722 11.5194 21 16 21ZM27.9643 39.2751L35.1312 32.1216L38 34.983L27.9643 45L20.7953 37.8444L23.6641 34.983L27.9622 39.2751H27.9643Z'
            fill='#1EBA09'
          />
        </svg>
      ),
      title: 'Employee Screening',
    },
    {
      content:
        'Physical mail is sorted, bar-coded, and tracked under strict security standards to ensure privacy is never compromised.',
      contentMaxWidth: '470px',
      icon: (
        <svg
          width='54'
          height='44'
          viewBox='0 0 54 44'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M49.2027 25.4737H44.2825V9.81432L24.7785 26.2564L4.92027 9.76337V37.0526H29.5216V41.6842H2.46014C1.80767 41.6842 1.18192 41.4402 0.720557 41.0059C0.259192 40.5716 0 39.9826 0 39.3684V2.31579C0 1.7016 0.259192 1.11257 0.720557 0.678279C1.18192 0.243984 1.80767 0 2.46014 0H46.7426C47.3951 0 48.0208 0.243984 48.4822 0.678279C48.9435 1.11257 49.2027 1.7016 49.2027 2.31579V25.4737ZM6.1774 4.63158L24.7514 20.0594L43.0573 4.63158H6.1774ZM41.8223 44L33.1233 35.8114L36.6044 32.5368L41.8223 37.451L50.5214 29.2623L54 32.5368L41.8223 44Z'
            fill='#DCB90F'
          />
        </svg>
      ),
      title: 'Mail Tracking',
    },
    {
      content:
        'Secure cross-shredding of confidential paperwork and provision of a Certificate of Destruction.',
      contentMaxWidth: '480px',
      icon: (
        <svg
          width='50'
          height='46'
          viewBox='0 0 50 46'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M44.2825 9.81432V25.4737H49.2027V2.31579C49.2027 1.7016 48.9435 1.11257 48.4822 0.678279C48.0208 0.243984 47.3951 0 46.7426 0H2.46014C1.80767 0 1.18193 0.243984 0.720562 0.678279C0.259197 1.11257 4.84872e-06 1.7016 4.84872e-06 2.31579V26H4.92028V9.76337L24.7785 26.2564L44.2825 9.81432ZM24.7514 20.0594L6.17741 4.63158H43.0573L24.7514 20.0594Z'
            fill='#BC2CC0'
          />
          <path
            d='M49 36.5263H44.1V28H49V36.5263ZM41.65 34.6316H36.75V28H41.65V34.6316ZM12.25 40.3158H7.35L7.35 28H12.25L12.25 40.3158ZM4.9 38.4211H0L1.20083e-06 28L4.9 28L4.9 38.4211ZM29.4 46H24.5L24.5 28H29.4L29.4 46ZM22.05 44.1053H17.15L17.15 28H22.05L22.05 44.1053Z'
            fill='#BC2CC0'
          />
        </svg>
      ),
      title: 'Secure Destruction',
    },
  ]
}

const KeyBenefitsSection: FC<KeyBenefitsSectionType> = ({
  tenant = Tenant.UK,
}) => {
  const benefits = getBenefits(tenant)

  return (
    <div className='py-6 overflow-x-lg-none overflow-x-auto auto pb-md-0'>
      <div className='row row-cols-1  flex-nowrap flex-md-wrap  row-cols-md-2 g-3 g-md-5 align-items-stretch'>
        {' '}
        {benefits.map(({ content, contentMaxWidth, icon, title }) => (
          <div className='benefit-card lh-140' key={title}>
            <Card padding='lg' shadow className='bg-white pe-4'>
              <div>{icon}</div>
              <h3 className='fs-fluid-20-28 mt-6 fw-semibold'>{title}</h3>
              <div
                className='fs-fluid-18-24 mt-2 fw-normal'
                style={
                  contentMaxWidth ? { maxWidth: contentMaxWidth } : undefined
                }
              >
                {content}
              </div>
            </Card>
          </div>
        ))}
      </div>
    </div>
  )
}

export default KeyBenefitsSection
